/**
 * Session Manager Utility
 * Handles session storage with browser-close expiry and inactivity timeout
 * 
 * Features:
 * - Uses sessionStorage (clears when browser closes)
 * - Tracks user activity and auto-logs out after inactivity
 * - Provides centralized session management
 */

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check every minute
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';

// Session keys that should expire on browser close
const SESSION_KEYS = [
  'user',
  'role', 
  'userLoggedIn',
  'userType',
  'sessionToken',
  'vendorInfo',
  'vendorToken',
  'vendor'
];

/**
 * Store session data in sessionStorage
 */
export const setSessionItem = (key, value) => {
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    sessionStorage.setItem(key, stringValue);
    updateLastActivity();
  } catch (error) {
    console.error('Error storing session item:', error);
  }
};

/**
 * Get session data from sessionStorage
 */
export const getSessionItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    console.error('Error getting session item:', error);
    return null;
  }
};

/**
 * Remove session data from sessionStorage
 */
export const removeSessionItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing session item:', error);
  }
};

/**
 * Clear all session data
 */
export const clearSession = () => {
  SESSION_KEYS.forEach(key => {
    sessionStorage.removeItem(key);
  });
  sessionStorage.removeItem(LAST_ACTIVITY_KEY);
};

/**
 * Update last activity timestamp
 */
export const updateLastActivity = () => {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
};

/**
 * Check if session has expired due to inactivity
 */
export const isSessionExpiredDueToInactivity = () => {
  const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  if (!lastActivity) return false;
  
  const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
  return timeSinceActivity > INACTIVITY_TIMEOUT;
};

/**
 * Get remaining time before inactivity logout (in minutes)
 */
export const getRemainingSessionTime = () => {
  const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  if (!lastActivity) return INACTIVITY_TIMEOUT / 60000;
  
  const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
  const remainingMs = Math.max(0, INACTIVITY_TIMEOUT - timeSinceActivity);
  return Math.ceil(remainingMs / 60000); // Return minutes
};

/**
 * Check if user is currently logged in
 */
export const isUserLoggedIn = () => {
  const userLoggedIn = sessionStorage.getItem('userLoggedIn');
  return userLoggedIn === 'true' && !isSessionExpiredDueToInactivity();
};

/**
 * Get current user data
 */
export const getCurrentUser = () => {
  try {
    const userData = sessionStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Get current user role
 */
export const getCurrentRole = () => {
  try {
    const roleData = sessionStorage.getItem('role');
    return roleData ? JSON.parse(roleData) : null;
  } catch (error) {
    console.error('Error parsing role data:', error);
    return null;
  }
};

/**
 * Get session token
 */
export const getSessionToken = () => {
  return sessionStorage.getItem('sessionToken');
};

/**
 * Get current plant ID from session
 */
export const getCurrentPlantId = () => {
  return sessionStorage.getItem('currentPlantId') || null;
};

/**
 * Set current plant ID in session (for plant switching)
 */
export const setCurrentPlantId = (plantId) => {
  sessionStorage.setItem('currentPlantId', plantId);
};

/**
 * Get available plants list
 */
export const getPlants = () => {
  try {
    const data = sessionStorage.getItem('plants');
    return data ? JSON.parse(data) : [];
  } catch { return []; }
};


/**
 * Initialize activity tracking
 * Call this once when app mounts
 */
let activityCheckInterval = null;
let onSessionExpiredCallback = null;

export const initActivityTracking = (onSessionExpired) => {
  onSessionExpiredCallback = onSessionExpired;
  
  // Update activity on user interactions
  const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
  
  const handleActivity = () => {
    if (isUserLoggedIn()) {
      updateLastActivity();
    }
  };
  
  activityEvents.forEach(event => {
    window.addEventListener(event, handleActivity, { passive: true });
  });
  
  // Periodic check for inactivity
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval);
  }
  
  activityCheckInterval = setInterval(() => {
    if (sessionStorage.getItem('userLoggedIn') === 'true' && isSessionExpiredDueToInactivity()) {
      if (onSessionExpiredCallback) {
        onSessionExpiredCallback('Your session has expired due to inactivity. Please login again.');
      }
    }
  }, ACTIVITY_CHECK_INTERVAL);
  
  // Return cleanup function
  return () => {
    activityEvents.forEach(event => {
      window.removeEventListener(event, handleActivity);
    });
    if (activityCheckInterval) {
      clearInterval(activityCheckInterval);
      activityCheckInterval = null;
    }
  };
};

/**
 * Store login data - convenience function
 */
export const storeLoginData = (data, isVendor = false) => {
  setSessionItem('user', data.user);
  setSessionItem('role', data.role);
  setSessionItem('userLoggedIn', 'true');
  setSessionItem('userType', isVendor ? 'vendor' : 'regular');
  
  if (data.session_token) {
    setSessionItem('sessionToken', data.session_token);
  }
  
  if (data.vendor) {
    setSessionItem('vendorInfo', data.vendor);
  }
  
  // Store plant info
  if (data.plants) {
    setSessionItem('plants', data.plants);
  }
  if (data.user?.plant_id) {
    setSessionItem('currentPlantId', data.user.plant_id);
  }
  
  updateLastActivity();
};

export default {
  setSessionItem,
  getSessionItem,
  removeSessionItem,
  clearSession,
  updateLastActivity,
  isSessionExpiredDueToInactivity,
  getRemainingSessionTime,
  isUserLoggedIn,
  getCurrentUser,
  getCurrentRole,
  getSessionToken,
  getCurrentPlantId,
  setCurrentPlantId,
  getPlants,
  initActivityTracking,
  storeLoginData
};
